
import {
  Badge,
  Link,
  Text,
} from '@chakra-ui/react';
import { Edit, Trash2, ExternalLink, Play } from 'lucide-react';
import { TableColumn, TableAction, ActionHandler } from '@/components/section/EntityTableSection';
import { ConditionResourceLookup } from '../api';
import RelativeTime from '@/components/ui/RelativeTime';
import { ConditionResourceAction } from '../hooks/useConditionResourceActions';
import { getStatusBadgeProps } from '@/utils/lookupStatus';

export const createVideosColumns = (): TableColumn<ConditionResourceLookup>[] => [
  {
    key: "id",
    title: "ID",
    variant: "id",
  },
  {
    key: "condition_lookup_id",
    title: "CONDITION",
    render: (resource) => (
      <Link
        href={`/conditions/#/${resource.condition_lookup_id}/view`}
        color="brand.500"
        fontSize="sm"
        fontWeight="medium"
      >
        #{resource.condition_lookup_id}
      </Link>
    ),
  },
  {
    key: "title",
    title: "TITLE",
    variant: "primary",
  },
  {
    key: "description",
    title: "DESCRIPTION",
    render: (resource) => (
      <Text 
        fontSize="sm" 
        color="gray.600" 
        lineClamp={2}
        maxW="300px"
      >
        {resource.description || 'No description available'}
      </Text>
    ),
  },
  
  {
    key: "url",
    title: "LINK",
    render: (resource) => (
      <Link
        href={resource.url}
        target="_blank"
        color="brand.500"
        fontSize="sm"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Play size={14} />
        Watch
        <ExternalLink size={12} />
      </Link>
    ),
  },
  {
    key: "status",
    title: "STATUS",
    render: (resource) => {
      const { colorPalette, label } = getStatusBadgeProps(resource.status);
      return <Badge colorPalette={colorPalette} size="sm">{label}</Badge>;
    },
  },
  {
    key: "is_valid",
    title: "VALID",
    render: (resource) => {
      if (resource.is_valid === true) {
        return <Badge colorPalette="green" size="sm">Valid</Badge>;
      } else if (resource.is_valid === false) {
        return <Badge colorPalette="red" size="sm">Invalid</Badge>;
      } else {
        return <Badge colorPalette="gray" size="sm">Unknown</Badge>;
      }
    },
  },
  {
    key: "created",
    title: "ADDED",
    render: (resource) => (
      <RelativeTime date={resource.created_at || new Date().toISOString()} />
    ),
  },
  {
    key: "updated",
    title: "UPDATED",
    render: (resource) => (
      <RelativeTime date={resource.updated_at || resource.created_at || new Date().toISOString()} />
    ),
  },
];

export const createVideosActions = (
  onAction: ActionHandler<ConditionResourceLookup, ConditionResourceAction>,
  isLoading: boolean
): TableAction<ConditionResourceLookup>[] => [
  {
    key: "edit",
    label: "Edit",
    icon: Edit,
    onClick: (entity) => onAction("edit", entity),
    disabled: isLoading,
  },
  {
    key: "delete",
    label: "Delete",
    icon: Trash2,
    onClick: (entity) => onAction("delete", entity),
    disabled: isLoading,
    colorPalette: "red",
  },
]; 